import { Currency, PhoneCountryCode } from '../../constants';
import type { ReviewMedia } from '../review/review';
import {
  OrderStatus,
  type DeliveryTrackingStatus,
  type ParxlTrackingStatus,
  type YunExpressTrackingStatus,
  type UnifiedShippingStatus,
} from '../../constants/order';

export type TrackingStatusType =
  | DeliveryTrackingStatus
  | ParxlTrackingStatus
  | YunExpressTrackingStatus
  | UnifiedShippingStatus
  | string;

export interface DeleoTraceEvent {
  detailStatus: string | null;
  eventTime: string;
  description: string;
  location: string | null;
  empNm: string | null;
  status: DeliveryTrackingStatus;
}

export interface TrackingEvent {
  id: string;
  type: TrackingStatusType;
  title: string;
  timestamp: string;
  description?: string;
  status: 'completed' | 'current' | 'pending';
}

export interface OrderLineItem {
  itemId: string;
  productId?: string;
  parentProductId?: string;
  name: string;
  nameKor?: string;
  originalName?: string;
  sku: string;
  quantity: number;
  originalQuantity: number;
  price?: number;
  total?: number;
  subtotal?: number;
  productSize?: string;
  productPack?: string;
  wooCommerceImageUrl?: string;
  inboundStatus?: string;
  sourcingRoute?: string;
  deliveryCompanyCd?: string;
  brand?: string;
  category?: string;
}

export interface OrderAddress {
  firstName: string;
  lastName: string;
  company?: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postcode: string;
  country: PhoneCountryCode;
  phone?: string;
  email?: string;
}

export interface DeleoData {
  deleoOrderId: string;
  deleoTrackingNo: string;
  deleoStatus: {
    name: string;
    code: string;
    desc: string;
  };
  deliveryTrace?: {
    orderId: string;
    status: {
      name: string;
      code: string;
      desc: string;
    };
    traceInfoList: Array<{
      mainTrackingNo: string;
      trackingNo: string;
      description: string;
      status: string;
      origin: string;
      dest: string;
      deliveryCompanyCode: {
        name: string;
        code: string;
        desc: string;
      };
      traceList: DeleoTraceEvent[];
    }>;
  };
  orderTrackingList?: Array<{
    trackingNo: string;
    deleoTrackingNo: string;
    deliveryCompanyCd: {
      name: string;
      code: string;
      desc: string;
    };
  }>;
  orderItemList?: Array<{
    orderItemId: string;
    itemCd: string;
    itemNm: string;
    quantity: number;
    amt: number;
    status: {
      name: string;
      code: string;
      desc: string;
    };
  }>;
}

export interface ParxlTrackingEvent {
  status: ParxlTrackingStatus;
  date: string;
  location: string;
}

export interface ParxlData {
  plsShipmentReference?: string;
  merchantBatchNo?: string;
  merchantOrderNo?: string;
  orderDate?: string;
  operationHub?: string;
  serviceOption?: string;
  latestTrackingStatus?: string;
  trackingStatus?: ParxlTrackingEvent[];
  weight?: number;
  weightUnit?: string;
  dimensions?: {
    length: number;
    width: number;
    height: number;
    unit: string;
  };
  incoterm?: string;
  containsBattery?: boolean;
  containsDg?: boolean;
  insured?: boolean;
  cod?: boolean;
  expirationDate?: string;
}

export interface YunExpressTrackEvent {
  processTime: string;
  processUtcTime: string;
  processContent: string;
  processCountry: string;
  processProvince: string;
  processCity: string;
  processLocation: string;
  trackNodeCode: string;
  trackNodeDescription?: string;
}

export interface YunExpressTrackInfo {
  waybillNumber?: string;
  trackingNumber?: string;
  customerOrderNumber?: string;
  trackEvents: YunExpressTrackEvent[];
}

export interface YunExpressData {
  waybillNumber: string;
  customerOrderNumber?: string;
  lastPackageStatus?: string;
  lastSyncedAt?: string;
  trackInfo?: YunExpressTrackInfo;
}

export type ShippingProvider = 'DELEO' | 'PARXL' | 'YUN_EXPRESS' | 'TRACKING_MORE';

export type ShippingStatus = 'PENDING' | 'IN_TRANSIT' | 'DELIVERED' | 'FAILED';

export interface ShippingData {
  provider: ShippingProvider;
  providerOrderId?: string;
  trackingNumber?: string;
  status?: ShippingStatus;
  statusCode?: string;
  statusDescription?: string;
  statusUpdatedAt?: string;
  labelUrl?: string;
  createdAt?: string;
  updatedAt?: string;
  // Unified tracking fields (prefer these over legacy carrier-specific data)
  unifiedStatus?: UnifiedShippingStatus;
  unifiedStatusUpdatedAt?: string;
  events?: UnifiedTrackingEventEntry[];
  statusHistory?: UnifiedStatusHistoryEntry[];
}

export interface UnifiedTrackingEventEntry {
  event: string;
  provider: string;
  providerEventCode: string;
  timestamp: string;
  description?: string;
  displayText: string;
  customerVisible: boolean;
}

export interface UnifiedStatusHistoryEntry {
  status: string;
  provider: string;
  providerStatusCode: string;
  timestamp: string;
}

export interface Order {
  orderId: string;
  status: OrderStatus;
  currency: Currency;
  totalAmount: number;
  discount?: number;
  shippingTotal?: number;
  couponLines?: Array<{ code: string; discount: number }>;
  loyaltyCreditApplied?: number;
  totalCustom?: number;
  orderDate: string;
  orderDateTime: string;
  createdAt: string;
  updatedAt: string;
  customerId: string;
  customer: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
  lineItems: OrderLineItem[];
  shippingAddress: OrderAddress;
  billingAddress: OrderAddress;
  deleoData?: DeleoData;
  parxlData?: ParxlData;
  yunExpressData?: YunExpressData;
  shippingData?: ShippingData;
  shippingMessage?: string;
  pic?: string;
}

/**
 * Customer-facing order response from the Facade endpoint:
 *   GET /woocommerce/customer/orders/:orderId
 *
 * Mirrors `CustomerWooCommerceOrderResponse` in SkinSeoulFacade — a slimmer
 * variant of `WooCommerceOrderResponse` with `metaData` and `links` stripped
 * (top-level and nested on line/tax/shipping/fee lines) to limit exposure of
 * internal data on the customer endpoint. Monetary values come through as
 * strings (WooCommerce REST convention) — parse at the consumer.
 */
export interface CustomerOrderTax {
  id: number;
  total: string;
  subtotal: string;
}

export interface CustomerOrderBilling {
  firstName: string;
  lastName: string;
  company?: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  email: string;
  phone: string;
}

export interface CustomerOrderShipping {
  firstName: string;
  lastName: string;
  company?: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  // Facade resolves this server-side: `shipping.phone || billing.phone || ''`.
  phone: string;
  // Surfaced by Facade from order meta `_shipping_tax_id`; empty string when absent.
  taxId: string;
}

export interface CustomerOrderLineItem {
  id: number;
  name: string;
  productId: number;
  variationId: number;
  quantity: number;
  taxClass: string;
  subtotal: string;
  subtotalTax: string;
  total: string;
  totalTax: string;
  taxes: CustomerOrderTax[];
  sku: string;
  price: number;
  wooCommerceImageUrl: string;
  // Enriched server-side from fulfilment order / RDS product lookup; may be absent.
  parentProductId?: number;
  productSize?: string;
  productPack?: string;
  nameKor?: string;
  originalSku?: string;
  regularPrice?: number;
  salePrice?: number;
  brand?: string;
  category?: string;
}

export interface CustomerOrderTaxLine {
  id: number;
  rateCode: string;
  rateId: number;
  label: string;
  compound: boolean;
  taxTotal: string;
  shippingTaxTotal: string;
}

export interface CustomerOrderShippingLine {
  id: number;
  methodTitle: string;
  methodId: string;
  total: string;
  totalTax: string;
  taxes: CustomerOrderTax[];
}

export interface CustomerOrderFeeLine {
  id: number;
  name: string;
  total: string;
}

export interface CustomerOrderCouponLine {
  code: string;
  discount: string;
}

export interface CustomerOrder {
  id: number;
  parentId: number;
  number: string;
  orderKey: string;
  createdVia: string;
  version: string;
  status: OrderStatus;
  currency: Currency;
  dateCreated: string;
  dateCreatedGmt: string;
  dateModified: string;
  dateModifiedGmt: string;
  discountTotal: string;
  discountTax: string;
  shippingTotal: string;
  shippingTax: string;
  cartTax: string;
  total: string;
  totalTax: string;
  totalCustom: string;
  pricesIncludeTax: boolean;
  customerId: number;
  customerIpAddress: string;
  customerUserAgent: string;
  customerNote: string;
  billing: CustomerOrderBilling;
  shipping: CustomerOrderShipping;
  paymentMethod: string;
  paymentMethodTitle: string;
  transactionId: string;
  datePaid?: string;
  datePaidGmt?: string;
  dateCompleted?: string;
  dateCompletedGmt?: string;
  cartHash: string;
  lineItems: CustomerOrderLineItem[];
  taxLines: CustomerOrderTaxLine[];
  shippingLines: CustomerOrderShippingLine[];
  feeLines: CustomerOrderFeeLine[];
  couponLines: CustomerOrderCouponLine[];
  refunds: unknown[];
  loyaltyCreditApplied: number;
  // Parity aliases + fulfilment tracking data merged server-side for frontend display.
  createdAt?: string;
  updatedAt?: string;
  countryCode?: string;
  deleoData?: DeleoData;
  parxlData?: ParxlData;
  yunExpressData?: YunExpressData;
  shippingData?: ShippingData;
}

export interface OrderDisplayItem {
  id: number;
  productId?: string;
  name: string;
  imageUrl?: string;
  variant: string;
  quantity: number;
  originalQuantity: number;
  price: number;
  subtotal?: number;
  total?: number;
  brand?: string;
  category?: string;
}

export interface OrderDisplay {
  id: number;
  orderNumber: string;
  status: OrderStatus;
  orderDate: string;
  deliveryDate?: string;
  trackingNumber: string;
  carrier: string | null;
  total: number;
  discount?: number;
  shippingTotal?: number;
  loyaltyCreditApplied?: number;
  totalCustom?: number;
  currency: Currency;
  customer: Order['customer'];
  shippingAddress: OrderAddress;
  billingAddress: OrderAddress;
  items: OrderDisplayItem[];
  totalQuantity: number;
  trackingEvents: TrackingEvent[];
  receiptUrl?: string;
}

export interface UserOrderLineItemReview {
  productId: number;
  reviewId: number;
  name: string;
  rating: number;
  body?: string;
  title?: string;
  pictureUrls: string[];
  media?: ReviewMedia[];
  skinType: string[];
  skinConcern: string[];
  status: string;
  modifiedAt: string;
}

/** Carried on the order list, so the order surfaces need no extra call. */
export interface UserOrderLineItemReviewCredit {
  eligible: boolean;
  alreadyCredited: boolean;
  creditType: 'text' | 'media';
  points: number;
  creditAmount: number;
  currency: Currency;
  fractionDigits: number;
  textPoints: number;
  textCreditAmount: number;
  mediaPoints: number;
  mediaCreditAmount: number;
}

export interface UserOrderLineItem {
  id?: number;
  productId?: number;
  name?: string;
  sku?: string;
  quantity?: number;
  productSize?: string;
  productPack?: string;
  brand?: string;
  category?: string;
  wooCommerceImageUrl?: string;
  reviewWritten: boolean;
  review?: UserOrderLineItemReview;
  reviewCredit?: UserOrderLineItemReviewCredit;
}

export interface UserOrderCard {
  orderId: string;
  status: string;
  orderedAt: string;
  itemCount: number;
  totalAmount: number;
  currency: Currency;
  shippingTotal?: number;
  shippingTax?: number;
  thumbnails: string[];
  lineItems?: UserOrderLineItem[];
}

export type AssociateOrderData = {
  orderId: number;
  userId: number;
  associatedAt: string;
  order: {
    id: number;
    status: string;
    total: string;
    currency: string;
    createdAt: string;
  };
};
